// Campaign Detail View Component
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBusinessAdminDashboard } from "@/hooks/business-admin/useBusinessAdminDashboard";
import { downloadCampaignPOCs, downloadCreativeMedia } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EditModal from "@/components/common-components/EditModal";
import CampaignStatusChangeDialog from "@/components/business-admin/CampaignStatusChangeDialog";
import {
  ArrowLeft,
  MapPin,
  Target,
  DollarSign,
  Calendar,
  Building2,
  Mail,
  Sparkles,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
  AlertCircle,
  Download,
  ArrowRight,
  Play,
  ChevronDown,
  Pencil,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";

const VITE_STATIC_URL = import.meta.env.VITE_STATIC_URL || "/";

// All available status options
const ALL_STATUS_OPTIONS = [
  { value: 'CREATED', label: 'Created', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { value: 'IN PROGRESS', label: 'In Progress', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { value: 'OUT FOR APPROVAL', label: 'Out for Approval', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  { value: 'LAUNCHED', label: 'Launched', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
  { value: 'PAUSED', label: 'Paused', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
];

const statusConfig = {
  LAUNCHED: { label: "LAUNCHED", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  "OUT FOR APPROVAL": { label: "OUT FOR APPROVAL", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  COMPLETED: { label: "COMPLETED", color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200" },
  CREATED: { label: "CREATED", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  "IN PROGRESS": { label: "IN PROGRESS", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  PAUSED: { label: "PAUSED", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

export default function CampaignDetailView() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { 
    selectedCampaign, 
    isLoading, 
    getCampaignDetail, 
    approveCampaign, 
    rejectCampaign,
    updateCampaignStatus,
    getNextStatus,
    getCampaignBasicInfo,
    getCampaignBudgetInfo,
    getCampaignPOCInfo,
    getCampaignCreativeInfo,
    getCampaignTargeting,
    getCampaignMedia,
    editCampaignName
  } = useBusinessAdminDashboard();

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [downloadingPOCs, setDownloadingPOCs] = useState(false);
  const [failedMediaIndices, setFailedMediaIndices] = useState([]);
  
  // Edit campaign name state
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editedCampaignName, setEditedCampaignName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    if (campaignId) {
      getCampaignDetail(parseInt(campaignId));
    }
  }, [campaignId]);

  const handleApprove = async () => {
    try {
      await approveCampaign(parseInt(campaignId));
      await getCampaignDetail(parseInt(campaignId));
      setShowApproveDialog(false);
    } catch (error) {
      console.error("Failed to approve campaign:", error);
    }
  };

  const handleReject = async () => {
    try {
      await rejectCampaign(parseInt(campaignId), rejectReason);
      await getCampaignDetail(parseInt(campaignId));
      setShowRejectDialog(false);
      setRejectReason("");
    } catch (error) {
      console.error("Failed to reject campaign:", error);
    }
  };

  const handleStatusChange = async () => {
    try {
      if (selectedNewStatus) {
        await updateCampaignStatus(parseInt(campaignId), selectedNewStatus);
        await getCampaignDetail(parseInt(campaignId));
        setShowStatusChangeDialog(false);
        setSelectedNewStatus(null);
      }
    } catch (error) {
      console.error("Failed to update campaign status:", error);
    }
  };

  const handleStatusSelect = (newStatus) => {
    setSelectedNewStatus(newStatus);
    setShowStatusChangeDialog(true);
  };

  const handleEditNameClick = () => {
    const basicInfo = getCampaignBasicInfo();
    setEditedCampaignName(basicInfo?.campaign_name || "");
    setShowEditNameModal(true);
  };

  const handleSaveCampaignName = async () => {
    if (!editedCampaignName.trim()) {
      return;
    }

    try {
      setIsEditingName(true);
      await editCampaignName(parseInt(campaignId), editedCampaignName);
      setShowEditNameModal(false);
      // Optionally refresh the campaign detail
      await getCampaignDetail(parseInt(campaignId));
    } catch (error) {
      console.error("Failed to update campaign name:", error);
      // You could add toast notification here
    } finally {
      setIsEditingName(false);
    }
  };

  const handleCloseEditModal = () => {
    if (!isEditingName) {
      setShowEditNameModal(false);
      setEditedCampaignName("");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto max-w-6xl px-6 py-8">
          <div className="animate-pulse space-y-8">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-muted" />
              <div className="h-8 w-64 rounded bg-muted" />
            </div>
            <div className="h-48 rounded-3xl bg-muted" />
            <div className="grid gap-6 md:grid-cols-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-64 rounded-3xl bg-muted" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedCampaign) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-lg font-semibold text-foreground">Campaign not found</p>
          <p className="text-sm text-muted-foreground">The campaign you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/business-dashboard")} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Extract data sections from the new API structure
  const basicInfo = getCampaignBasicInfo();
  const budgetInfo = getCampaignBudgetInfo();
  const pocInfo = getCampaignPOCInfo();
  const creativeInfo = getCampaignCreativeInfo();
  const targeting = getCampaignTargeting();
  const media = getCampaignMedia();

  // Use basic_info for main campaign data
  const statusInfo = statusConfig[basicInfo?.status] || basicInfo?.status_display || statusConfig.CREATED;
  
  // Get other status options (exclude current status)
  const otherStatusOptions = ALL_STATUS_OPTIONS.filter(status => status.value !== basicInfo?.status);

  // Handle creative media download – uses backend API so it works in prod (files on API server)
  const handleDownloadImage = async (imageUrl, index) => {
    const filenameFromUrl = imageUrl.split('/').pop() || `creative-media-${index + 1}.jpg`;
    try {
      const response = await downloadCreativeMedia(imageUrl);
      const contentType = response.headers.get('Content-Type') || '';
      // Backend may return 200 with JSON body on error (e.g. file not found)
      if (contentType.includes('application/json') || !response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition?.match(/filename="?([^";\n]+)"?/)?.[1] || filenameFromUrl;
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading creative media:', error);
      try {
        const fullUrl = `${VITE_STATIC_URL}${imageUrl}`;
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = filenameFromUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);
        alert(error.message || 'Unable to download file. It may be missing on the server.');
      }
    }
  };

  // Handle download all images
  const handleDownloadAllImages = async () => {
    if (media?.files && media.files.length > 0) {
      for (let i = 0; i < media.files.length; i++) {
        await handleDownloadImage(media.files[i].url, i);
        // Add a small delay between downloads to avoid browser blocking
        if (i < media.files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  // Handle download POCs as CSV
  const handleDownloadPOCs = async () => {
    if (!campaignId) return;
    
    setDownloadingPOCs(true);
    try {
      const result = await downloadCampaignPOCs(campaignId);
      const ok = result.success === true || result.status === true;
      const dataPayload = result.data;

      if (ok && dataPayload && dataPayload.columns && Array.isArray(dataPayload.pocs)) {
        const { columns, pocs } = dataPayload;

        // Create CSV content
        let csvContent = columns.join(',') + '\n';

        pocs.forEach((row) => {
          const rowData = columns.map((col) => {
            const key = col.toLowerCase().replace(/ /g, '_');
            const value = row[key] !== undefined && row[key] !== null ? row[key] : '';
            return typeof value === 'string' && (value.includes(',') || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          });
          csvContent += rowData.join(',') + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `campaign_${campaignId}_pocs_${new Date().toISOString().split('T')[0]}.csv`;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        throw new Error(result.message || 'Failed to download POCs');
      }
    } catch (error) {
      console.error('Error downloading POCs:', error);
      alert('Failed to download POCs: ' + error.message);
    } finally {
      setDownloadingPOCs(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 pt-24">
      {/* Main Content */}
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/business-dashboard")}
                className="rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1 block">
                  REQUEST {basicInfo?.campaign_id || 'N/A'}
                </span>
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-black text-foreground">{basicInfo?.campaign_name || 'Untitled Campaign'}</h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEditNameClick}
                    className="h-8 w-8 rounded-full hover:bg-primary/10"
                    title="Edit campaign name"
                  >
                    <Pencil className="h-4 w-4 text-primary" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={statusInfo.color + " text-xs font-bold uppercase tracking-wider px-3 py-1"}>
                {statusInfo.label || statusInfo.display || 'UNKNOWN'}
              </Badge>
              
              {/* Status Change Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    Change Status
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Update Campaign Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {otherStatusOptions.map((status) => (
                    <DropdownMenuItem 
                      key={status.value}
                      onClick={() => handleStatusSelect(status.value)}
                      className="cursor-pointer"
                    >
                      <Badge className={status.color + " text-xs font-medium mr-2"}>
                        {status.label}
                      </Badge>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="space-y-8 pb-32">
          {/* Section 1: Core Info */}
          <div className="bg-muted/50 p-6 rounded-3xl border border-border/40 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1 flex items-center gap-1">
                  <Target className="h-3 w-3" /> Objective
                </p>
                <p className="font-bold text-foreground">
                  {basicInfo?.campaign_objective || basicInfo?.campaign_type || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Duration
                </p>
                <p className="font-bold text-foreground text-sm">
                  {basicInfo?.start_date && new Date(basicInfo.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {basicInfo?.end_date && ` — ${new Date(basicInfo.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  {!basicInfo?.start_date && !basicInfo?.end_date && 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Utilized / Total Budget
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-black text-primary">
                    {budgetInfo?.formatted_utilized_budget || `$${budgetInfo?.utilized_budget || 0}`}
                  </p>
                  <span className="text-muted-foreground font-bold">/</span>
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                    {budgetInfo?.formatted_total_budget || `$${budgetInfo?.total_budget || 0}`}
                  </p>
                </div>
                {budgetInfo?.utilization_rate && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {budgetInfo.utilization_rate}% utilized
                  </p>
                )}
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                  Rep Info
                </p>
                {/* <p className="font-bold text-foreground text-sm">{basicInfo?.bdr_email?.split('@')[0] || "N/A"}</p> */}
                <p className="font-bold text-foreground text-sm">{basicInfo?.bdr_email || ""}</p>
              </div>
            </div>
          </div>

          {/* Section 2: Additional Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Brand & Region Info */}
            <div className="bg-card p-6 rounded-3xl border border-border/40 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Campaign Details
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                    Brand
                  </p>
                  <p className="font-bold text-foreground">{basicInfo?.brand || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                    Region
                  </p>
                  <p className="font-bold text-foreground">{basicInfo?.region || 'N/A'}</p>
                </div>
                {basicInfo?.territory && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      Territory
                    </p>
                    <p className="font-bold text-foreground">{basicInfo.territory}</p>
                  </div>
                )}
                {basicInfo?.role && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      Role
                    </p>
                    <p className="font-bold text-foreground">{basicInfo.role}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Budget Breakdown */}
            <div className="bg-card p-6 rounded-3xl border border-border/40 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Budget Breakdown
              </h3>
              <div className="space-y-3">
                {/* Budget Allocation Method */}
                {budgetInfo?.budget_allocation_mode && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      Allocation Method
                    </p>
                    <div className="flex items-center gap-2">
                      {budgetInfo.budget_allocation_mode === 'Budget Optimized' ? (
                        <>
                          <Badge className="bg-primary/10 text-primary border-primary/20 px-2 py-0.5 text-xs font-bold flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> Budget Optimized
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="outline" className="px-2 py-0.5 text-xs font-bold">
                          Budget Per POC
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show Budget Per POC only if NOT Budget Optimized */}
                {budgetInfo?.budget_per_poc && budgetInfo.budget_per_poc > 0 && budgetInfo.budget_allocation_mode !== 'Budget Optimized' && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      Budget per POC
                    </p>
                    <p className="text-xl font-black text-foreground">{budgetInfo.formatted_budget_per_poc || `$${budgetInfo.budget_per_poc}`}</p>
                  </div>
                )}

                {budgetInfo?.gl_code && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      GL Code
                    </p>
                    <p className="font-mono text-sm font-bold text-foreground">{budgetInfo.gl_code}</p>
                  </div>
                )}
                {budgetInfo?.cost_center && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      Cost Center
                    </p>
                    <p className="font-mono text-sm font-bold text-foreground">{budgetInfo.cost_center}</p>
                  </div>
                )}
                {basicInfo?.bdr_email && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                      BDR Contact
                    </p>
                    <a 
                      href={`mailto:${basicInfo.bdr_email}`} 
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" />
                      {basicInfo.bdr_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: POC Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Point of Contacts ({pocInfo?.actual_pocs || pocInfo?.pocs?.length || basicInfo?.no_of_pocs || basicInfo?.actual_pocs_count || 0})
              </h3>
              <div className="flex items-center gap-2">
                {budgetInfo?.budget_allocation_mode === 'Budget Optimized' && (
                  <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1 text-[10px] font-black uppercase flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI Optimized Budgets
                  </Badge>
                )}
                {pocInfo?.pocs && pocInfo.pocs.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPOCs}
                    disabled={downloadingPOCs}
                    className="h-8 text-xs font-bold"
                  >
                    <Download className="h-3 w-3 mr-1.5" />
                    {downloadingPOCs ? 'Downloading...' : 'Download POCs'}
                  </Button>
                )}
              </div>
            </div>
            <div className="border border-border/40 rounded-2xl overflow-hidden shadow-sm bg-card">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 font-bold text-muted-foreground uppercase text-[10px] tracking-wider">
                        POC ID
                      </th>
                      <th className="px-4 py-3 font-bold text-muted-foreground uppercase text-[10px] tracking-wider">
                        Location
                      </th>
                      <th className="px-4 py-3 font-bold text-muted-foreground uppercase text-[10px] tracking-wider">
                        Region
                      </th>
                      <th className="px-4 py-3 font-bold text-muted-foreground uppercase text-[10px] tracking-wider text-right">
                        Allocated Budget
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {pocInfo?.pocs && pocInfo.pocs.length > 0 ? (
                      pocInfo.pocs.map((poc, index) => {
                        // Use the poc_budget from the POC object directly
                        const pocBudget = poc.poc_budget || poc.budget || 0;
                        const formattedBudget = poc.formatted_budget || `$${pocBudget.toLocaleString()}`;
                        
                        return (
                          <tr key={poc.poc_id || poc.id || index} className="bg-card hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <p className="font-mono text-xs font-bold text-foreground">{poc.poc_id || 'N/A'}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-bold text-foreground">{poc.poc_name || poc.name || poc.location || 'N/A'}</p>
                              {poc.address && (
                                <p className="text-xs text-muted-foreground mt-0.5">{poc.address}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-muted-foreground">
                                {poc.region || poc.state || "—"}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <p className="font-black text-foreground">{formattedBudget}</p>
                              {budgetInfo?.budget_allocation_mode === 'Budget Optimized' && (
                                <p className="text-[10px] text-muted-foreground">optimized</p>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">
                          <div className="flex flex-col items-center gap-2">
                            <MapPin className="h-8 w-8 text-muted-foreground/50" />
                            <p>No POCs selected for this campaign</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Section 4: Creative Preview */}
          {creativeInfo && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-primary" />
                Creative Content
              </h3>
              <div className="bg-card p-6 rounded-3xl border border-border/40 shadow-sm space-y-6">
                {/* Ad Headline */}
                {creativeInfo.ad_headline && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">
                      Ad Headline
                    </p>
                    <h4 className="text-xl font-bold text-foreground">
                      {creativeInfo.ad_headline}
                    </h4>
                  </div>
                )}

                {/* Body Copy */}
                {creativeInfo.body_copy && (
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">
                      Body Copy
                    </p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {creativeInfo.body_copy}
                    </p>
                  </div>
                )}

                {/* Media Files */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                      Media Assets
                    </p>
                    {media?.files && media.files.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadAllImages}
                        className="h-8 text-xs"
                      >
                        <Download className="h-3 w-3 mr-1.5" />
                        Download All ({media.files.length})
                      </Button>
                    )}
                  </div>
                  {media?.files && media.files.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {media.files.map((mediaFile, index) => (
                        <div key={index} className="border border-border/40 rounded-xl overflow-hidden bg-muted/30 group relative">
                          {failedMediaIndices.includes(index) ? (
                            <div className="w-full aspect-square flex items-center justify-center bg-muted/30">
                              <div className="text-center p-4">
                                <ImageIcon className="w-12 h-12 mx-auto mb-2 text-muted-foreground/50" />
                                <p className="text-xs text-muted-foreground">{`Media ${index + 1}`}</p>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={`${VITE_STATIC_URL}${mediaFile.url}`}
                              alt={`Creative media ${index + 1}`}
                              className="w-full aspect-square object-cover"
                              onError={() => {
                                setFailedMediaIndices((prev) =>
                                  prev.includes(index) ? prev : [...prev, index]
                                );
                              }}
                            />
                          )}
                          {/* Download button overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleDownloadImage(mediaFile.url, index)}
                              className="shadow-lg"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-border/60 rounded-xl p-8 bg-muted/20">
                      <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">No media files uploaded</p>
                          <p className="text-xs text-muted-foreground mt-1">No creative assets have been added to this campaign</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Creative Info */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border/40">
                  {creativeInfo?.cta && (
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                        Call to Action
                      </p>
                      <p className="text-sm font-bold text-foreground">
                        {creativeInfo.cta}
                      </p>
                    </div>
                  )}
                  {(targeting?.min_age || targeting?.max_age) && (
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                        Target Age Range
                      </p>
                      <p className="text-sm font-bold text-foreground">
                        {targeting.min_age} - {targeting.max_age} years
                      </p>
                    </div>
                  )}
                  {creativeInfo?.creative_type && (
                    <div>
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">
                        Creative Type
                      </p>
                      <p className="text-sm font-bold text-foreground">
                        {creativeInfo.creative_type}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer Actions */}
      {/* {basicInfo?.status === "OUT FOR APPROVAL" && (
        <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border shadow-2xl z-50">
          <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/business-dashboard")}
                className="flex-1 py-6 font-bold rounded-2xl text-base"
              >
                Close Detail
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRejectDialog(true)}
                className="flex-1 py-6 font-bold rounded-2xl text-base"
              >
                <XCircle className="mr-2 h-5 w-5" />
                Reject Campaign
              </Button>
              <Button
                onClick={() => setShowApproveDialog(true)}
                className="flex-1 py-6 bg-emerald-600 hover:bg-emerald-700 font-bold rounded-2xl shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20 text-base"
              >
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Approve Campaign
              </Button>
            </div>
          </div>
        </div>
      )} */}

      {/* Approve Dialog */}
      {/* <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Approve Campaign
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <p>Are you sure you want to approve this campaign?</p>
              <div className="mt-4 p-3 bg-muted rounded-lg text-left">
                <p className="font-semibold text-foreground text-sm">
                  {basicInfo?.campaign_name || 'Untitled Campaign'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {basicInfo?.campaign_type || 'N/A'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs font-medium">{budgetInfo?.formatted_total_budget || `$${budgetInfo?.total_budget || 0}`}</span>
                </div>
              </div>
              <p className="text-sm pt-2">
                This action will launch the campaign and make it live.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApprove}
              className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}

      {/* Reject Dialog */}
      {/* <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Reject Campaign
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <p>Please provide a reason for rejecting this campaign.</p>
              <div className="mt-4 p-3 bg-muted rounded-lg text-left">
                <p className="font-semibold text-foreground text-sm">
                  {basicInfo?.campaign_name || 'Untitled Campaign'}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Rejection Reason <span className="text-destructive">*</span>
            </label>
            <Textarea
              placeholder="Enter a detailed reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This reason will be shared with the campaign creator.
            </p>
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={() => setRejectReason("")}
              className="mt-0"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={!rejectReason.trim()}
              className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 disabled:opacity-50"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}

      {/* Status Change Confirmation Dialog */}
      <CampaignStatusChangeDialog
        open={showStatusChangeDialog}
        onOpenChange={(open) => {
          setShowStatusChangeDialog(open);
          if (!open) {
            setSelectedNewStatus(null);
          }
        }}
        campaignName={basicInfo?.campaign_name}
        currentStatusLabel={statusInfo.label}
        currentStatusBadgeClassName=""
        newStatusLabel={ALL_STATUS_OPTIONS.find(s => s.value === selectedNewStatus)?.label || selectedNewStatus}
        newStatusBadgeClassName={
          ALL_STATUS_OPTIONS.find(s => s.value === selectedNewStatus)?.color ||
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
        }
        onConfirm={handleStatusChange}
      />

      {/* Edit Campaign Name Modal */}
      <EditModal
        isOpen={showEditNameModal}
        onClose={handleCloseEditModal}
        title="Edit Campaign Name"
        description="Update the campaign name below."
        label="Campaign Name"
        value={editedCampaignName}
        onChange={setEditedCampaignName}
        onSave={handleSaveCampaignName}
        isLoading={isEditingName}
        placeholder="Enter campaign name"
      />
    </div>
  );
}
